import { useState, useEffect, useCallback } from 'react';
import { useAccessibility } from '@/hooks/useAccessibility';
import { AccessibilityPanel } from '@/components/base/AccessibilityPanel';
import { useToast } from '@/hooks/useToast';
import { ApiException } from '@/utils/errorHandler';
import {
  getAllSettings,
  updateLanguage,
  updateAccessibility,
  restoreAccessibility,
  updateNotifications,
  updateContactChannels,
  updateTokenConfig,
  get2FAStatus,
  enable2FA,
  confirmEnable2FA,
  change2FAMethod,
  disable2FA,
  updatePrivacy,
  getSessions,
  terminateSession,
  terminateOtherSessions,
  getAccessHistory,
  closeAccount,
  revertAccountClosure,
  type SessionItem,
  type AccessHistoryItem,
  type NotificacoesResponse,
  type ContactChannelsResponse,
  type TokenConfigResponse,
  type PrivacyResponse,
  type TwoFactorStatusResponse,
} from '@/services/accountSettings';

export default function ConfiguracoesPage() {
  const { settings, increaseFontSize, decreaseFontSize, toggleLargeCursor, resetSettings } = useAccessibility();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [acessibilidadeAberta, setAcessibilidadeAberta] = useState(false);

  // Idioma
  const [idiomaSelecionado, setIdiomaSelecionado] = useState('pt-BR');

  // Notificações
  const [notificacoes, setNotificacoes] = useState<NotificacoesResponse>({
    creditoPontos: true,
    creditoXP: true,
    rastreioEnvio: true,
    novosDesafios: true,
    novasCampanhas: true,
    distintivosConquistados: true,
    lembretesMissoes: false,
    atualizacoesRanking: true,
  });

  // Preferências de Contato
  const [preferenciasContato, setPreferenciasContato] = useState<ContactChannelsResponse>({
    email: true,
    sms: false,
    push: true,
    whatsapp: false,
  });

  // Token Config
  const [tokenConfig, setTokenConfig] = useState<TokenConfigResponse>({ email: true, sms: false });

  // 2FA
  const [twoFactorStatus, setTwoFactorStatus] = useState<TwoFactorStatusResponse>({
    ativo: false,
    metodo: 'email',
    metodosDisponiveis: ['email', 'sms', 'app'],
  });
  const [twoFactorTemp, setTwoFactorTemp] = useState<TwoFactorStatusResponse>(twoFactorStatus);
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  const [twoFactorStep, setTwoFactorStep] = useState<'select' | 'confirm'>('select');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  // Sessões
  const [sessoes, setSessoes] = useState<SessionItem[]>([]);
  const [encerrandoSessao, setEncerrandoSessao] = useState<string | null>(null);
  const [showEncerrarTodasModal, setShowEncerrarTodasModal] = useState(false);

  // Histórico
  const [historico, setHistorico] = useState<AccessHistoryItem[]>([]);
  const [mostrarTodoHistorico, setMostrarTodoHistorico] = useState(false);

  // Privacidade
  const [privacidade, setPrivacidade] = useState<PrivacyResponse>({
    perfilVisivel: 'todos',
    rankingVisivel: true,
    feedVisivel: true,
    conquistasVisiveis: true,
    pontosVisiveis: false,
  });

  // Encerramento de conta
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteForm, setDeleteForm] = useState({ senha: '', confirmacao: '', motivo: '' });
  const [deleting, setDeleting] = useState(false);
  const [closureResult, setClosureResult] = useState<{ protocolo: string; prazoDias: number; dataLimite: string } | null>(null);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [reverting, setReverting] = useState(false);

  // Carregar dados
  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const data = await getAllSettings();
        setIdiomaSelecionado(data.idioma);
        setNotificacoes(data.notificacoes);
        setPreferenciasContato(data.preferenciasContato);
        setTokenConfig(data.tokenConfig);
        setTwoFactorStatus(data.autenticacao2FA);
        setTwoFactorTemp(data.autenticacao2FA);
        setPrivacidade(data.privacidade);
      } catch (err) {
        const msg = err instanceof ApiException ? err.message : 'Erro ao carregar configurações';
        toast.error('Erro ao carregar configurações', msg);
      }

      try {
        const sessData = await getSessions();
        setSessoes(sessData.sessoes);
      } catch (err) {
        const msg = err instanceof ApiException ? err.message : 'Erro ao carregar sessões';
        toast.error('Erro ao carregar sessões', msg);
      }

      try {
        const histData = await getAccessHistory(1, 50);
        setHistorico(histData.acessos);
      } catch (err) {
        const msg = err instanceof ApiException ? err.message : 'Erro ao carregar histórico';
        toast.error('Erro ao carregar histórico', msg);
      }

      setLoading(false);
    }
    load();
  }, []);

  // ─── Idioma ───
  const handleChangeLanguage = useCallback(async (lang: string) => {
    setIdiomaSelecionado(lang);
    setSaving('idioma');
    try {
      await updateLanguage(lang);
      toast.success('Idioma atualizado', `O idioma foi alterado para ${lang}`);
    } catch (err) {
      const msg = err instanceof ApiException ? err.message : 'Erro ao atualizar idioma';
      toast.error('Erro ao atualizar idioma', msg);
    }
    setSaving(null);
  }, [toast]);

  // ─── Acessibilidade ───
  const handleAccessibilityChange = useCallback(async (key: 'fontSize' | 'largeCursor', action?: () => void) => {
    if (action) action();
    const body = {
      tamanhoFonte: settings.fontSize,
      cursorMaior: settings.largeCursor,
    };
    setSaving('acessibilidade');
    try {
      await updateAccessibility(body);
    } catch (err) {
      const msg = err instanceof ApiException ? err.message : 'Erro ao salvar acessibilidade';
      toast.error('Erro ao salvar acessibilidade', msg);
    }
    setSaving(null);
  }, [settings, toast]);

  const handleRestoreAccessibility = useCallback(async () => {
    resetSettings();
    setSaving('acessibilidade');
    try {
      await restoreAccessibility();
      toast.success('Configurações restauradas', 'As configurações de acessibilidade foram restauradas para o padrão.');
    } catch (err) {
      const msg = err instanceof ApiException ? err.message : 'Erro ao restaurar';
      toast.error('Erro ao restaurar', msg);
    }
    setSaving(null);
  }, [resetSettings, toast]);

  // ─── Notificações ───
  const handleToggleNotificacao = useCallback(async (key: keyof NotificacoesResponse) => {
    const next = { ...notificacoes, [key]: !notificacoes[key] };
    setNotificacoes(next);
    setSaving('notificacoes');
    try {
      await updateNotifications(next);
      toast.success('Notificações atualizadas', 'Suas preferências de notificação foram salvas.');
    } catch (err) {
      const msg = err instanceof ApiException ? err.message : 'Erro ao salvar';
      toast.error('Erro ao salvar', msg);
    }
    setSaving(null);
  }, [notificacoes, toast]);

  // ─── Preferências de Contato ───
  const handleToggleContato = useCallback(async (key: keyof ContactChannelsResponse) => {
    const next = { ...preferenciasContato, [key]: !preferenciasContato[key] };
    setPreferenciasContato(next);
    setSaving('contato');
    try {
      await updateContactChannels(next);
      toast.success('Preferências de contato atualizadas', 'Suas preferências de contato foram salvas.');
    } catch (err) {
      const msg = err instanceof ApiException ? err.message : 'Erro ao salvar';
      toast.error('Erro ao salvar', msg);
    }
    setSaving(null);
  }, [preferenciasContato, toast]);

  // ─── Token Config ───
  const handleToggleToken = useCallback(async (key: 'email' | 'sms') => {
    const next: TokenConfigResponse = { email: key === 'email', sms: key === 'sms' };
    setTokenConfig(next);
    setSaving('token');
    try {
      await updateTokenConfig(next);
      toast.success('Token atualizado', 'A configuração de token foi salva.');
    } catch (err) {
      const msg = err instanceof ApiException ? err.message : 'Erro ao salvar';
      toast.error('Erro ao salvar', msg);
    }
    setSaving(null);
  }, [toast]);

  // ─── 2FA ───
  const handleIniciarEdicao2FA = useCallback(() => {
    setTwoFactorTemp({ ...twoFactorStatus });
    setTwoFactorStep('select');
    setTwoFactorCode('');
    setBackupCodes(null);
    setShowTwoFactorModal(true);
  }, [twoFactorStatus]);

  const handleToggle2FA = useCallback(async (tipo: 'email' | 'sms' | 'app') => {
    if (!twoFactorStatus.ativo) {
      // Habilitar
      setSaving('2fa');
      try {
        const res = await enable2FA(tipo);
        setTwoFactorTemp({ ...twoFactorTemp, metodo: tipo });
        setTwoFactorStep('confirm');
        toast.info('Código enviado', `Verifique ${res.destinoMascarado}`);
      } catch (err) {
        const msg = err instanceof ApiException ? err.message : 'Erro ao ativar 2FA';
        toast.error('Erro ao ativar 2FA', msg);
      }
      setSaving(null);
    } else {
      // Alterar método
      setSaving('2fa');
      try {
        await change2FAMethod(tipo, twoFactorCode);
        setTwoFactorStatus({ ...twoFactorStatus, metodo: tipo });
        setShowTwoFactorModal(false);
        toast.success('2FA atualizado', 'O método de autenticação foi alterado.');
      } catch (err) {
        const msg = err instanceof ApiException ? err.message : 'Erro ao alterar 2FA';
        toast.error('Erro ao alterar 2FA', msg);
      }
      setSaving(null);
    }
  }, [twoFactorStatus, twoFactorTemp, twoFactorCode, toast]);

  const handleConfirmar2FA = useCallback(async () => {
    setSaving('2fa');
    try {
      const res = await confirmEnable2FA(twoFactorCode);
      setTwoFactorStatus({ ...twoFactorStatus, ativo: true, metodo: twoFactorTemp.metodo });
      setBackupCodes(res.codigosBackup);
      toast.success('2FA ativado', 'Autenticação de dois fatores ativada com sucesso.');
    } catch (err) {
      const msg = err instanceof ApiException ? err.message : 'Código inválido';
      toast.error('Código inválido', msg);
    }
    setSaving(null);
  }, [twoFactorCode, twoFactorStatus, twoFactorTemp, toast]);

  const handleDesativar2FA = useCallback(async () => {
    setSaving('2fa');
    try {
      await disable2FA(twoFactorCode);
      setTwoFactorStatus({ ...twoFactorStatus, ativo: false });
      setShowTwoFactorModal(false);
      toast.success('2FA desativado', 'Autenticação de dois fatores foi desativada.');
    } catch (err) {
      const msg = err instanceof ApiException ? err.message : 'Erro ao desativar 2FA';
      toast.error('Erro ao desativar 2FA', msg);
    }
    setSaving(null);
  }, [twoFactorCode, twoFactorStatus, toast]);

  // ─── Privacidade ───
  const handleTogglePrivacidade = useCallback(async (key: keyof PrivacyResponse) => {
    const next = { ...privacidade, [key]: !privacidade[key] };
    setPrivacidade(next);
    setSaving('privacidade');
    try {
      await updatePrivacy(next);
      toast.success('Privacidade atualizada', 'Suas preferências de privacidade foram salvas.');
    } catch (err) {
      const msg = err instanceof ApiException ? err.message : 'Erro ao salvar';
      toast.error('Erro ao salvar', msg);
    }
    setSaving(null);
  }, [privacidade, toast]);

  const handlePerfilVisivel = useCallback(async (value: PrivacyResponse['perfilVisivel']) => {
    const next = { ...privacidade, perfilVisivel: value };
    setPrivacidade(next);
    setSaving('privacidade');
    try {
      await updatePrivacy(next);
      toast.success('Privacidade atualizada', 'A visibilidade do perfil foi alterada.');
    } catch (err) {
      const msg = err instanceof ApiException ? err.message : 'Erro ao salvar';
      toast.error('Erro ao salvar', msg);
    }
    setSaving(null);
  }, [privacidade, toast]);

  // ─── Sessões ───
  const handleEncerrarSessao = useCallback(async (id: string) => {
    setEncerrandoSessao(id);
    try {
      await terminateSession(id);
      setSessoes(prev => prev.filter(s => s.id !== id));
      toast.success('Sessão encerrada', 'A sessão foi encerrada com sucesso.');
    } catch (err) {
      const msg = err instanceof ApiException ? err.message : 'Erro ao encerrar sessão';
      toast.error('Erro ao encerrar sessão', msg);
    }
    setEncerrandoSessao(null);
  }, [toast]);

  const handleEncerrarTodas = useCallback(async () => {
    setShowEncerrarTodasModal(false);
    try {
      const res = await terminateOtherSessions();
      setSessoes(prev => prev.filter(s => s.atual));
      toast.success('Sessões encerradas', `${res.sessoesEncerradas} sessões foram encerradas.`);
    } catch (err) {
      const msg = err instanceof ApiException ? err.message : 'Erro ao encerrar sessões';
      toast.error('Erro ao encerrar sessões', msg);
    }
  }, [toast]);

  // ─── Encerramento de Conta ───
  const handleEncerrarConta = useCallback(async () => {
    if (!deleteForm.senha || !deleteForm.confirmacao) {
      toast.error('Preencha todos os campos', 'Informe sua senha e confirmação.');
      return;
    }
    setDeleting(true);
    try {
      const res = await closeAccount(deleteForm.senha, deleteForm.confirmacao, deleteForm.motivo);
      setClosureResult({
        protocolo: res.protocolo,
        prazoDias: res.prazoDias,
        dataLimite: res.dataLimiteReversao,
      });
      setShowDeleteModal(false);
      toast.success('Conta encerrada', `Protocolo: ${res.protocolo}`);
    } catch (err) {
      const msg = err instanceof ApiException ? err.message : 'Erro ao encerrar conta';
      toast.error('Erro ao encerrar conta', msg);
    }
    setDeleting(false);
  }, [deleteForm, toast]);

  const handleReverterEncerramento = useCallback(async () => {
    setReverting(true);
    try {
      await revertAccountClosure();
      setClosureResult(null);
      setShowRevertModal(false);
      toast.success('Reversão concluída', 'O encerramento da conta foi revertido.');
    } catch (err) {
      const msg = err instanceof ApiException ? err.message : 'Erro ao reverter';
      toast.error('Erro ao reverter', msg);
    }
    setReverting(false);
  }, [toast]);

  // ─── UI helpers ───
  const fontSizeLabel = {
    normal: 'Normal (100%)',
    large: 'Grande (115%)',
    xlarge: 'Extra Grande (130%)',
  }[settings.fontSize];

  const handleCloseAccessibility = useCallback(() => setAcessibilidadeAberta(false), []);

  const historicoExibido = mostrarTodoHistorico ? historico : historico.slice(0, 5);

  if (loading) {
    return (
      <div className="w-full space-y-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 overflow-hidden animate-pulse">
            <div className="px-6 py-4 bg-slate-50 h-16"></div>
            <div className="p-6 space-y-4">
              <div className="h-4 bg-slate-100 rounded w-3/4"></div>
              <div className="h-4 bg-slate-100 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* ── IDIOMA ── */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-teal-50 to-cyan-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
              <i className="ri-translate-2 text-xl text-teal-600"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Idioma</h2>
              <p className="text-sm text-slate-600">Escolha o idioma da plataforma</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { value: 'pt-BR', label: 'Português (BR)', flag: '🇧🇷' },
              { value: 'en-US', label: 'English (US)', flag: '🇺🇸' },
              { value: 'es-ES', label: 'Español', flag: '🇪🇸' },
            ].map((lang) => (
              <button
                key={lang.value}
                onClick={() => handleChangeLanguage(lang.value)}
                disabled={saving === 'idioma'}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all cursor-pointer ${
                  idiomaSelecionado === lang.value
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-slate-200 hover:border-slate-300'
                } ${saving === 'idioma' ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span className="text-2xl">{lang.flag}</span>
                <span className={`text-sm font-semibold ${idiomaSelecionado === lang.value ? 'text-teal-800' : 'text-slate-700'}`}>
                  {lang.label}
                </span>
                {idiomaSelecionado === lang.value && (
                  <i className="ri-check-line text-teal-600 ml-auto"></i>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── ACESSIBILIDADE ── */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-orange-50 to-amber-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <i className="ri-eye-line text-xl text-orange-600"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Acessibilidade</h2>
              <p className="text-sm text-slate-600">Personalize a experiência visual</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {/* Tamanho da fonte inline */}
          <div className="flex items-center justify-between py-3 border-b border-slate-100">
            <div>
              <p className="text-sm font-semibold text-slate-800">Tamanho da Fonte</p>
              <p className="text-xs text-slate-500 mt-0.5">{fontSizeLabel}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleAccessibilityChange('fontSize', decreaseFontSize)}
                disabled={settings.fontSize === 'normal' || saving === 'acessibilidade'}
                className="w-8 h-8 flex items-center justify-center border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer text-slate-600 transition-colors"
              >
                <i className="ri-subtract-line text-sm"></i>
              </button>
              <div className="flex gap-1">
                {(['normal', 'large', 'xlarge'] as const).map((size) => (
                  <div
                    key={size}
                    className={`w-2 h-2 rounded-full transition-colors ${settings.fontSize === size ? 'bg-orange-500' : 'bg-slate-200'}`}
                  />
                ))}
              </div>
              <button
                onClick={() => handleAccessibilityChange('fontSize', increaseFontSize)}
                disabled={settings.fontSize === 'xlarge' || saving === 'acessibilidade'}
                className="w-8 h-8 flex items-center justify-center border border-orange-200 bg-orange-50 rounded-lg hover:bg-orange-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer text-orange-600 transition-colors"
              >
                <i className="ri-add-line text-sm"></i>
              </button>
            </div>
          </div>

          {/* Cursor maior */}
          <div className="flex items-center justify-between py-3 border-b border-slate-100">
            <div>
              <p className="text-sm font-semibold text-slate-800">Cursor Maior</p>
              <p className="text-xs text-slate-500 mt-0.5">Aumenta o cursor e área de clique</p>
            </div>
            <button
              onClick={() => handleAccessibilityChange('largeCursor', toggleLargeCursor)}
              disabled={saving === 'acessibilidade'}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                settings.largeCursor ? 'bg-orange-600' : 'bg-slate-300'
              } ${saving === 'acessibilidade' ? 'opacity-50' : ''}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.largeCursor ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          <div className="flex items-center justify-between pt-1">
            <button
              onClick={handleRestoreAccessibility}
              disabled={saving === 'acessibilidade'}
              className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 cursor-pointer transition-colors disabled:opacity-50"
            >
              <i className="ri-refresh-line"></i>
              Restaurar padrões
            </button>
            <button
              onClick={() => setAcessibilidadeAberta(true)}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg text-xs font-semibold hover:bg-orange-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-settings-3-line"></i>
              Painel Completo
            </button>
          </div>
        </div>
      </div>

      {/* ── PRIVACIDADE ── */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-violet-50 to-purple-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
              <i className="ri-shield-user-line text-xl text-violet-600"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Preferências de Privacidade</h2>
              <p className="text-sm text-slate-600">Controle quem pode ver suas informações</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {/* Visibilidade do perfil */}
          <div>
            <p className="text-sm font-semibold text-slate-800 mb-3">Visibilidade do Perfil no Feed</p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'todos' as const, label: 'Todos', icon: 'ri-global-line' },
                { value: 'amigos' as const, label: 'Amigos', icon: 'ri-group-line' },
                { value: 'ninguem' as const, label: 'Privado', icon: 'ri-lock-line' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => handlePerfilVisivel(opt.value)}
                  disabled={saving === 'privacidade'}
                  className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border-2 transition-all cursor-pointer ${
                    privacidade.perfilVisivel === opt.value
                      ? 'border-violet-500 bg-violet-50'
                      : 'border-slate-200 hover:border-slate-300'
                  } ${saving === 'privacidade' ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <i className={`${opt.icon} text-xl ${privacidade.perfilVisivel === opt.value ? 'text-violet-600' : 'text-slate-400'}`}></i>
                  <span className={`text-xs font-semibold ${privacidade.perfilVisivel === opt.value ? 'text-violet-800' : 'text-slate-600'}`}>
                    {opt.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-slate-100 pt-4 space-y-3">
            {[
              { key: 'rankingVisivel' as const, label: 'Aparecer no Ranking', desc: 'Sua posição fica visível para outros participantes' },
              { key: 'feedVisivel' as const, label: 'Atividade no Feed Social', desc: 'Suas conquistas aparecem no feed de amigos' },
              { key: 'conquistasVisiveis' as const, label: 'Exibir Distintivos', desc: 'Outros podem ver seus distintivos conquistados' },
              { key: 'pontosVisiveis' as const, label: 'Exibir Pontuação', desc: 'Sua pontuação fica visível no perfil público' },
            ].map(({ key, label, desc }) => (
              <div key={key} className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium text-slate-800">{label}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                </div>
                <button
                  onClick={() => handleTogglePrivacidade(key)}
                  disabled={saving === 'privacidade'}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer flex-shrink-0 ${
                    privacidade[key] ? 'bg-violet-600' : 'bg-slate-300'
                  } ${saving === 'privacidade' ? 'opacity-50' : ''}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacidade[key] ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── SESSÕES ATIVAS ── */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-sky-50 to-blue-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center">
                <i className="ri-device-line text-xl text-sky-600"></i>
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">Sessões Ativas</h2>
                <p className="text-sm text-slate-600">{sessoes.length} dispositivo{sessoes.length !== 1 ? 's' : ''} conectado{sessoes.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
            {sessoes.length > 1 && (
              <button
                onClick={() => setShowEncerrarTodasModal(true)}
                className="px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap border border-red-200"
              >
                Encerrar outras
              </button>
            )}
          </div>
        </div>
        <div className="p-6 space-y-3">
          {sessoes.map((sessao) => (
            <div
              key={sessao.id}
              className={`flex items-start gap-4 p-4 rounded-xl border transition-all ${
                sessao.atual ? 'border-sky-200 bg-sky-50' : 'border-slate-200 bg-white'
              }`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${sessao.atual ? 'bg-sky-100' : 'bg-slate-100'}`}>
                <i className={`ri-computer-line text-xl ${sessao.atual ? 'text-sky-600' : 'text-slate-500'}`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-slate-900">{sessao.dispositivo}</p>
                  {sessao.atual && (
                    <span className="px-2 py-0.5 bg-sky-600 text-white text-[10px] font-bold rounded-full whitespace-nowrap">
                      Sessão atual
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{sessao.navegador} · {sessao.sistema}</p>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5">
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <i className="ri-map-pin-line"></i>
                    {sessao.localizacao}
                  </span>
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <i className="ri-wifi-line"></i>
                    {sessao.ip}
                  </span>
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {sessao.ultimoAcesso}
                  </span>
                </div>
              </div>
              {!sessao.atual && (
                <button
                  onClick={() => handleEncerrarSessao(sessao.id)}
                  disabled={encerrandoSessao === sessao.id}
                  className="px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap border border-red-200 flex items-center gap-1 disabled:opacity-50"
                >
                  {encerrandoSessao === sessao.id ? (
                    <i className="ri-loader-4-line animate-spin"></i>
                  ) : (
                    <i className="ri-logout-box-line"></i>
                  )}
                  Encerrar
                </button>
              )}
            </div>
          ))}
          {sessoes.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              <i className="ri-device-line text-3xl mb-2"></i>
              <p className="text-sm">Nenhuma sessão ativa</p>
            </div>
          )}
        </div>
      </div>

      {/* ── HISTÓRICO DE ACESSOS ── */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <i className="ri-history-line text-xl text-slate-600"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Histórico de Acessos</h2>
              <p className="text-sm text-slate-600">Últimos logins na sua conta</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="space-y-2">
            {historicoExibido.map((acesso) => (
              <div key={acesso.id} className="flex items-center gap-3 py-3 border-b border-slate-100 last:border-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  acesso.status === 'sucesso' ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  <i className={`text-sm ${acesso.status === 'sucesso' ? 'ri-check-line text-green-600' : 'ri-close-line text-red-600'}`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{acesso.dispositivo}</p>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <i className="ri-map-pin-line"></i>
                      {acesso.localizacao}
                    </span>
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <i className="ri-wifi-line"></i>
                      {acesso.ip}
                    </span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs font-semibold text-slate-700">{acesso.data}</p>
                  <p className="text-xs text-slate-500">{acesso.hora}</p>
                </div>
                {acesso.status === 'falha' && (
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded-full whitespace-nowrap">
                    Falha
                  </span>
                )}
              </div>
            ))}
          </div>
          {historico.length > 5 && (
            <button
              onClick={() => setMostrarTodoHistorico(!mostrarTodoHistorico)}
              className="w-full mt-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-colors cursor-pointer flex items-center justify-center gap-2"
            >
              <i className={mostrarTodoHistorico ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
              {mostrarTodoHistorico ? 'Ver menos' : `Ver todos (${historico.length})`}
            </button>
          )}
        </div>
      </div>

      {/* ── NOTIFICAÇÕES ── */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-purple-50 to-pink-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <i className="ri-notification-3-line text-xl text-purple-600"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Notificações</h2>
              <p className="text-sm text-slate-600">Gerencie suas preferências de notificação</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-1">
          {(Object.entries({
            creditoPontos: 'Crédito de Pontos',
            creditoXP: 'Crédito de XP',
            rastreioEnvio: 'Rastreio de Envio de prêmios físicos',
            novosDesafios: 'Novos Desafios',
            novasCampanhas: 'Novas Campanhas',
            distintivosConquistados: 'Distintivos Conquistados',
            lembretesMissoes: 'Lembretes de Missões',
            atualizacoesRanking: 'Atualizações de Ranking',
          }) as [keyof NotificacoesResponse, string][]).map(([key, label]) => (
            <div key={key} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
              <span className="text-sm font-medium text-slate-700">{label}</span>
              <button
                onClick={() => handleToggleNotificacao(key)}
                disabled={saving === 'notificacoes'}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                  notificacoes[key] ? 'bg-purple-600' : 'bg-slate-300'
                } ${saving === 'notificacoes' ? 'opacity-50' : ''}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificacoes[key] ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* ── PREFERÊNCIAS DE CONTATO ── */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-cyan-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <i className="ri-message-3-line text-xl text-blue-600"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Preferências de Contato</h2>
              <p className="text-sm text-slate-600">Escolha como deseja ser contatado</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-1">
          {(Object.entries({
            email: 'E-mail',
            sms: 'SMS',
            push: 'Push (navegador)',
            whatsapp: 'WhatsApp',
          }) as [keyof ContactChannelsResponse, string][]).map(([key, label]) => (
            <div key={key} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
              <span className="text-sm font-medium text-slate-700">{label}</span>
              <button
                onClick={() => handleToggleContato(key)}
                disabled={saving === 'contato'}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                  preferenciasContato[key] ? 'bg-blue-600' : 'bg-slate-300'
                } ${saving === 'contato' ? 'opacity-50' : ''}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${preferenciasContato[key] ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* ── CONFIGURAÇÃO DE TOKEN ── */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-green-50 to-emerald-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <i className="ri-key-2-line text-xl text-green-600"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Configuração de Token</h2>
              <p className="text-sm text-slate-600">Defina como receber tokens de acesso</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-1">
          {(Object.entries({ email: 'Token por E-mail', sms: 'Token por SMS' }) as [keyof TokenConfigResponse, string][]).map(([key, label]) => (
            <div key={key} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
              <span className="text-sm font-medium text-slate-700">{label}</span>
              <button
                onClick={() => handleToggleToken(key)}
                disabled={saving === 'token'}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                  tokenConfig[key] ? 'bg-green-600' : 'bg-slate-300'
                } ${saving === 'token' ? 'opacity-50' : ''}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${tokenConfig[key] ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* ── 2FA ── */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-orange-50 to-amber-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <i className="ri-shield-check-line text-xl text-orange-600"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Autenticação de 2 Fatores</h2>
              <p className="text-sm text-slate-600">Adicione uma camada extra de segurança</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-slate-100">
            <div>
              <span className="text-sm font-medium text-slate-700">Ativar 2FA</span>
              <p className="text-xs text-slate-500 mt-1">
                {twoFactorStatus.ativo
                  ? `Ativo via ${twoFactorStatus.metodo === 'email' ? 'E-mail' : twoFactorStatus.metodo === 'sms' ? 'SMS' : 'App Autenticador'}`
                  : 'Desativado — recomendamos ativar para maior segurança'}
              </p>
            </div>
            <button
              onClick={handleIniciarEdicao2FA}
              className="px-3 py-1.5 text-xs font-semibold text-orange-600 hover:bg-orange-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap border border-orange-200"
            >
              {twoFactorStatus.ativo ? 'Gerenciar' : 'Ativar'}
            </button>
          </div>
        </div>
      </div>

      {/* ── ZONA DE PERIGO ── */}
      <div className="bg-white rounded-xl border border-red-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-red-200 bg-gradient-to-r from-red-50 to-rose-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <i className="ri-alert-line text-xl text-red-600"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Zona de Perigo</h2>
              <p className="text-sm text-slate-600">Ações irreversíveis</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          {closureResult ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex gap-3">
                <i className="ri-error-warning-line text-amber-600 text-xl flex-shrink-0"></i>
                <div>
                  <h3 className="text-sm font-semibold text-amber-900 mb-1">Conta em processo de encerramento</h3>
                  <p className="text-xs text-amber-700">
                    Protocolo: <strong>{closureResult.protocolo}</strong><br/>
                    Prazo de reversão: <strong>{closureResult.prazoDias} dias</strong> (até {new Date(closureResult.dataLimite).toLocaleDateString('pt-BR')})
                  </p>
                  <button
                    onClick={() => setShowRevertModal(true)}
                    className="mt-3 px-4 py-2 bg-amber-600 text-white text-xs font-semibold rounded-lg hover:bg-amber-700 transition-colors cursor-pointer"
                  >
                    Reverter Encerramento
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex gap-3">
                  <i className="ri-error-warning-line text-red-600 text-xl flex-shrink-0"></i>
                  <div>
                    <h3 className="text-sm font-semibold text-red-900 mb-1">Atenção!</h3>
                    <p className="text-xs text-red-700">O encerramento da conta é permanente e resultará na perda de todos os seus dados, pontos, distintivos e histórico.</p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="w-full px-4 py-3 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-delete-bin-line"></i>
                Encerrar Conta
              </button>
            </>
          )}
        </div>
      </div>

      {/* Modal 2FA */}
      {showTwoFactorModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-slate-700 to-slate-900 px-6 py-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <i className="ri-shield-check-line text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">Autenticação de 2 Fatores</h3>
                    <p className="text-xs text-slate-300 mt-0.5">
                      {twoFactorStep === 'select' ? 'Escolha o método' : 'Confirme o código'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowTwoFactorModal(false); setBackupCodes(null); }}
                  className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg hover:bg-white/30 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {twoFactorStep === 'select' && (
                <>
                  <p className="text-sm text-slate-600 mb-2">
                    {twoFactorStatus.ativo
                      ? 'Escolha o novo método de verificação:'
                      : 'Escolha como deseja receber os códigos de verificação:'}
                  </p>
                  {[
                    { value: 'email' as const, label: 'E-mail', icon: 'ri-mail-line', desc: 'Receber código via e-mail' },
                    { value: 'sms' as const, label: 'SMS', icon: 'ri-message-2-line', desc: 'Receber código via SMS' },
                    { value: 'app' as const, label: 'App Autenticador', icon: 'ri-smartphone-line', desc: 'Google Authenticator ou similar' },
                  ].map((method) => (
                    <button
                      key={method.value}
                      onClick={() => handleToggle2FA(method.value)}
                      disabled={saving === '2fa'}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all cursor-pointer text-left ${
                        twoFactorTemp.metodo === method.value
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-slate-200 hover:border-slate-300'
                      } ${saving === '2fa' ? 'opacity-50' : ''}`}
                    >
                      <i className={`${method.icon} text-lg ${twoFactorTemp.metodo === method.value ? 'text-orange-600' : 'text-slate-400'}`}></i>
                      <div>
                        <p className={`text-sm font-medium ${twoFactorTemp.metodo === method.value ? 'text-orange-900' : 'text-slate-700'}`}>{method.label}</p>
                        <p className="text-xs text-slate-500">{method.desc}</p>
                      </div>
                    </button>
                  ))}
                  {twoFactorStatus.ativo && (
                    <div className="pt-2 border-t border-slate-100">
                      <button
                        onClick={() => setTwoFactorStep('confirm')}
                        className="w-full py-2 text-xs text-red-600 font-semibold hover:bg-red-50 rounded-lg transition-colors"
                      >
                        Desativar 2FA
                      </button>
                    </div>
                  )}
                </>
              )}

              {twoFactorStep === 'confirm' && (
                <>
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center">
                    <i className="ri-mail-send-line text-3xl text-orange-500 mb-2"></i>
                    <p className="text-sm font-semibold text-slate-900 mb-1">Código enviado!</p>
                    <p className="text-xs text-slate-600">
                      Digite o código de verificação para confirmar.
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-slate-700 mb-2 block">Código de Verificação</label>
                    <input
                      type="text"
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg text-center text-2xl font-bold tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  {backupCodes && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-sm font-semibold text-green-900 mb-2">Códigos de backup</p>
                      <p className="text-xs text-green-700 mb-2">Guarde esses códigos em um local seguro. Eles permitem acesso caso você perca seu método principal.</p>
                      <div className="grid grid-cols-2 gap-2">
                        {backupCodes.map((code, i) => (
                          <span key={i} className="px-2 py-1 bg-white rounded text-xs font-mono text-green-800 border border-green-200">
                            {code}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-3 pt-1">
                    <button
                      onClick={() => setTwoFactorStep('select')}
                      className="flex-1 px-4 py-3 bg-white border-2 border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors cursor-pointer text-sm"
                    >
                      Voltar
                    </button>
                    {twoFactorStatus.ativo ? (
                      <button
                        onClick={handleDesativar2FA}
                        disabled={saving === '2fa'}
                        className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors cursor-pointer text-sm disabled:opacity-70"
                      >
                        {saving === '2fa' ? 'Aguarde...' : 'Desativar'}
                      </button>
                    ) : (
                      <button
                        onClick={handleConfirmar2FA}
                        disabled={saving === '2fa'}
                        className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors cursor-pointer text-sm disabled:opacity-70"
                      >
                        {saving === '2fa' ? 'Aguarde...' : 'Confirmar'}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal encerrar conta */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-3xl text-red-600"></i>
            </div>
            <h3 className="text-xl font-bold text-slate-900 text-center mb-2">Encerrar Conta?</h3>
            <p className="text-sm text-slate-600 text-center mb-4">Esta ação é irreversível. Todos os seus dados, pontos, distintivos e histórico serão permanentemente excluídos.</p>

            <div className="space-y-3 mb-4">
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Senha atual</label>
                <input
                  type="password"
                  value={deleteForm.senha}
                  onChange={(e) => setDeleteForm(prev => ({ ...prev, senha: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Digite sua senha"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Digite "ENCERRAR" para confirmar</label>
                <input
                  type="text"
                  value={deleteForm.confirmacao}
                  onChange={(e) => setDeleteForm(prev => ({ ...prev, confirmacao: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="ENCERRAR"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Motivo (opcional)</label>
                <textarea
                  value={deleteForm.motivo}
                  onChange={(e) => setDeleteForm(prev => ({ ...prev, motivo: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Conte-nos por que está saindo..."
                  rows={3}
                  maxLength={500}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setShowDeleteModal(false)} className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer">Cancelar</button>
              <button
                onClick={handleEncerrarConta}
                disabled={deleting}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer disabled:opacity-70"
              >
                {deleting ? 'Processando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal reverter encerramento */}
      {showRevertModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
            <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-arrow-go-back-line text-2xl text-amber-600"></i>
            </div>
            <h3 className="text-lg font-bold text-slate-900 text-center mb-2">Reverter Encerramento?</h3>
            <p className="text-sm text-slate-600 text-center mb-5">A reversão cancelará o processo de encerramento e sua conta voltará a ficar ativa imediatamente.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowRevertModal(false)} className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-lg transition-colors cursor-pointer">Cancelar</button>
              <button
                onClick={handleReverterEncerramento}
                disabled={reverting}
                className="flex-1 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer disabled:opacity-70"
              >
                {reverting ? 'Processando...' : 'Reverter'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal encerrar todas as sessões */}
      {showEncerrarTodasModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-logout-box-line text-2xl text-red-600"></i>
            </div>
            <h3 className="text-lg font-bold text-slate-900 text-center mb-2">Encerrar outras sessões?</h3>
            <p className="text-sm text-slate-600 text-center mb-5">Todos os outros dispositivos serão desconectados. Apenas sua sessão atual permanecerá ativa.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowEncerrarTodasModal(false)} className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-lg transition-colors cursor-pointer">Cancelar</button>
              <button onClick={handleEncerrarTodas} className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer">Encerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* Painel de acessibilidade completo */}
      <AccessibilityPanel isOpen={acessibilidadeAberta} onClose={handleCloseAccessibility} />
    </div>
  );
}